import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Store } from '@Models';
import { getRetailerName } from '../../../FieldServices/constants';
import RetailerTree, { FilterScope, SelectionState } from '../components/retailerTree';
import StepHeader from '../components/stepHeader';
import StoreTable from '../components/storeTable';
import { getLockedRetailer, getStoreKey, isStoreSelectable } from '../constants';
import { ScheduleWizardWhereState, SelectedStoreKey } from '../scheduleWizard.types';

interface Step1WhereProps {
  stores: Store[];
  value: ScheduleWizardWhereState;
  onChange: (value: ScheduleWizardWhereState) => void;
}

const toSelectedStoreKey = (store: Store): SelectedStoreKey => ({
  retailer: store.retailer,
  location_code: store.location_code,
});

const Step1Where = ({ stores, value, onChange }: Step1WhereProps) => {
  const { t } = useTranslation();
  const [scope, setScope] = useState<FilterScope>({ kind: 'all' });
  const [expandedRetailers, setExpandedRetailers] = useState<Set<string>>(new Set());
  const [search, setSearch] = useState('');

  const selectedKeys = useMemo(() => new Set(value.selectedStores.map(getStoreKey)), [value.selectedStores]);
  const lockedRetailer = getLockedRetailer(value);
  const oneRetailerHint = t('Schedule.Wizard.Where.OneRetailerHint');
  const isSelectable = useCallback((store: Store): boolean => isStoreSelectable(value, store), [value]);

  const storesByRetailer = useMemo(() => {
    const map = new Map<string, Store[]>();
    stores.forEach((store) => {
      const existing = map.get(store.retailer);
      if (existing) existing.push(store);
      else map.set(store.retailer, [store]);
    });
    return map;
  }, [stores]);

  const retailers = useMemo(
    () => Array.from(storesByRetailer.keys()).sort((a, b) => getRetailerName(a).localeCompare(getRetailerName(b))),
    [storesByRetailer],
  );

  const regionsByRetailer = useMemo(() => {
    const map = new Map<string, string[]>();
    storesByRetailer.forEach((retailerStores, retailer) => {
      const regions = Array.from(
        new Set(retailerStores.map((store) => store.region).filter((region): region is string => !!region)),
      ).sort();
      map.set(retailer, regions);
    });
    return map;
  }, [storesByRetailer]);

  const getSelectionState = (targetStores: Store[]): SelectionState => {
    if (targetStores.length === 0) return { checked: false, indeterminate: false };
    const selectedCount = targetStores.reduce(
      (count, store) => (selectedKeys.has(getStoreKey(store)) ? count + 1 : count),
      0,
    );
    return {
      checked: selectedCount === targetStores.length,
      indeterminate: selectedCount > 0 && selectedCount < targetStores.length,
    };
  };

  const setStoresSelected = (targetStores: Store[], selected: boolean): void => {
    const targetKeys = new Set(targetStores.map(getStoreKey));
    const remaining = value.selectedStores.filter((entry) => !targetKeys.has(getStoreKey(entry)));
    if (!selected) {
      onChange({ selectedStores: remaining });
      return;
    }
    const retailer = lockedRetailer ?? targetStores[0]?.retailer;
    const added = targetStores.filter((store) => store.retailer === retailer).map(toSelectedStoreKey);
    onChange({ selectedStores: [...remaining, ...added] });
  };

  const toggleStore = useCallback(
    (store: Store): void => {
      const key = getStoreKey(store);
      if (selectedKeys.has(key)) {
        onChange({ selectedStores: value.selectedStores.filter((entry) => getStoreKey(entry) !== key) });
      } else if (isSelectable(store)) {
        onChange({ selectedStores: [...value.selectedStores, toSelectedStoreKey(store)] });
      }
    },
    [selectedKeys, value, onChange, isSelectable],
  );

  const getScopeStores = (targetScope: FilterScope): Store[] => {
    switch (targetScope.kind) {
      case 'all':
        return stores;
      case 'retailer':
        return storesByRetailer.get(targetScope.retailer) ?? [];
      default:
        return (storesByRetailer.get(targetScope.retailer) ?? []).filter(
          (store) => store.region === targetScope.region,
        );
    }
  };

  const handleAllRetailersClick = (): void => {
    setScope({ kind: 'all' });
    setExpandedRetailers(new Set());
  };

  const handleRetailerRowClick = (retailer: string): void => {
    setScope({ kind: 'retailer', retailer });
    setExpandedRetailers((prev) => (prev.has(retailer) ? prev : new Set(prev).add(retailer)));
  };

  const toggleRetailerExpanded = (retailer: string): void => {
    setExpandedRetailers((prev) => {
      const next = new Set(prev);
      if (next.has(retailer)) next.delete(retailer);
      else next.add(retailer);
      return next;
    });
  };

  const handleRegionRowClick = (retailer: string, region: string): void =>
    setScope({ kind: 'region', retailer, region });

  const scopedStores = useMemo(() => getScopeStores(scope), [scope, stores, storesByRetailer]);

  const searchedStores = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return scopedStores;
    return scopedStores.filter(
      (store) => (store.name ?? '').toLowerCase().includes(query) || (store.region ?? '').toLowerCase().includes(query),
    );
  }, [scopedStores, search]);

  const selectableSearchedStores = searchedStores.filter(isSelectable);
  const allSearchedSelected =
    selectableSearchedStores.length > 0 &&
    selectableSearchedStores.every((store) => selectedKeys.has(getStoreKey(store)));
  const searchedRetailerCount = new Set(searchedStores.map((store) => store.retailer)).size;
  const selectAllDisabled = lockedRetailer === null ? searchedRetailerCount > 1 : selectableSearchedStores.length === 0;

  const handleToggleScopeSelectAll = (): void => setStoresSelected(selectableSearchedStores, !allSearchedSelected);

  return (
    <div className="flex flex-col gap-4">
      <StepHeader
        title={t('Schedule.Wizard.Steps.where')}
        subtitle={t('Schedule.Wizard.Where.SectionSubtitle')}
        rightSlot={
          <span className="whitespace-nowrap rounded-full border border-brand-200 bg-brand-50 px-3 py-1 text-xs font-semibold text-brand-700">
            {t('Schedule.Wizard.Where.StoresSelected', { count: value.selectedStores.length })}
          </span>
        }
      />

      <div className="flex overflow-hidden rounded-xl border border-gray-200 bg-white">
        <RetailerTree
          stores={stores}
          storesByRetailer={storesByRetailer}
          regionsByRetailer={regionsByRetailer}
          retailers={retailers}
          scope={scope}
          expandedRetailers={expandedRetailers}
          lockedRetailer={lockedRetailer}
          oneRetailerHint={oneRetailerHint}
          retailersLabel={t('Schedule.Wizard.Where.RetailersLabel')}
          allRetailersLabel={t('Schedule.Wizard.Where.AllRetailers')}
          getSelectionState={getSelectionState}
          onAllRetailersClick={handleAllRetailersClick}
          onRetailerRowClick={handleRetailerRowClick}
          onToggleRetailerExpanded={toggleRetailerExpanded}
          onRegionRowClick={handleRegionRowClick}
          onToggleStoresSelected={setStoresSelected}
        />
        <StoreTable
          search={search}
          onSearchChange={setSearch}
          searchedStores={searchedStores}
          selectedKeys={selectedKeys}
          isSelectable={isSelectable}
          oneRetailerHint={oneRetailerHint}
          onToggleStore={toggleStore}
          selectableCount={selectableSearchedStores.length}
          allSearchedSelected={allSearchedSelected}
          selectAllDisabled={selectAllDisabled}
          onToggleSelectAll={handleToggleScopeSelectAll}
        />
      </div>
    </div>
  );
};

export default Step1Where;
